import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../config/supabaseClient';
import FormField from '../components/ui/FormField';
import Input from '../components/ui/Input';
import Button from '../components/ui/Button';
import './Settings.css';

/* ─────────────────────────────────────────────────────────────
   SETTINGS PAGE

   Props:
   - theme        (string) 'dark' | 'light'
   - toggleTheme  (fn)     switches and persists the theme
   ───────────────────────────────────────────────────────────── */
function Settings({ theme, setTheme, toggleTheme }) {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();

  // ── Saved (DB) profile values ──────────────────────────────
  const [savedFirstName, setSavedFirstName] = useState('');
  const [savedLastName,  setSavedLastName]  = useState('');
  const [savedUsername,  setSavedUsername]  = useState('');

  // ── Draft values (only used while editing) ─────────────────
  const [firstName, setFirstName] = useState('');
  const [lastName,  setLastName]  = useState('');
  const [username,  setUsername]  = useState('');

  // Username availability: null | 'checking' | 'available' | 'taken'
  const [usernameStatus, setUsernameStatus] = useState(null);

  // ── Profile section edit flow ──────────────────────────────
  // 'view'    → showing read-only values, Edit button visible
  // 'confirm' → showing "Are you sure you want to edit?" prompt
  // 'edit'    → fields are editable, Save / Cancel visible
  const [profileMode, setProfileMode] = useState('view');

  // ── UI state ───────────────────────────────────────────────
  const [loading,      setLoading]      = useState(true);
  const [saving,       setSaving]       = useState(false);
  const [saveSuccess,  setSaveSuccess]  = useState(false);
  const [saveError,    setSaveError]    = useState('');
  const [profileError, setProfileError] = useState('');

  // ── Danger zone ────────────────────────────────────────────
  // 'idle'  → just the Delete button
  // 'open'  → password + username confirmation form
  // 'final' → last warning screen before actual deletion
  const [deleteStep,     setDeleteStep]     = useState('idle');
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteUsername, setDeleteUsername] = useState('');
  const [deleteError,    setDeleteError]    = useState('');
  const [deleting,       setDeleting]       = useState(false);

  // ── Load profile on mount ──────────────────────────────────
  useEffect(() => {
    if (user) loadProfile();
  }, [user]);

  async function loadProfile() {
    setLoading(true);
    const { data, error } = await supabase
      .from('profiles')
      .select('first_name, last_name, username')
      .eq('id', user.id)
      .single();

    if (error) {
      setProfileError('Could not load your profile. Please refresh.');
    } else {
      setSavedFirstName(data.first_name || '');
      setSavedLastName(data.last_name   || '');
      setSavedUsername(data.username    || '');
    }
    setLoading(false);
  }

  // ── Enter edit mode ────────────────────────────────────────
  // Copy saved values into draft fields so the user edits from
  // the current state, then switch to edit mode.
  function enterEditMode() {
    setFirstName(savedFirstName);
    setLastName(savedLastName);
    setUsername(savedUsername);
    setUsernameStatus(null);
    setSaveError('');
    setSaveSuccess(false);
    setProfileMode('edit');
  }

  // ── Cancel editing ─────────────────────────────────────────
  function cancelEdit() {
    setProfileMode('view');
    setSaveError('');
    setSaveSuccess(false);
  }

  // ── Username: enforce allowed chars on every keystroke ─────
  function handleUsernameChange(e) {
    const cleaned = e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '');
    setUsername(cleaned);
    setUsernameStatus(null);
  }

  // ── Real-time availability check (debounced 400ms) ─────────
  // Skip the check if the username matches the saved value.
  useEffect(() => {
    if (profileMode !== 'edit') return;
    if (username === savedUsername) { setUsernameStatus(null); return; }
    if (username.length < 3)        { setUsernameStatus(null); return; }

    setUsernameStatus('checking');
    const timer = setTimeout(async () => {
      const { data } = await supabase
        .from('profiles')
        .select('id')
        .eq('username', username)
        .neq('id', user.id)
        .maybeSingle();
      setUsernameStatus(data ? 'taken' : 'available');
    }, 400);
    return () => clearTimeout(timer);
  }, [username, profileMode]);

  // ── Save profile ───────────────────────────────────────────
  async function handleSave(e) {
    e.preventDefault();
    setSaveError('');
    setSaveSuccess(false);

    if (!firstName.trim()) { setSaveError('First name is required.'); return; }
    if (!lastName.trim())  { setSaveError('Last name is required.');  return; }
    if (!username.trim())  { setSaveError('Username is required.');   return; }
    if (username.length < 3)  { setSaveError('Username must be at least 3 characters.'); return; }
    if (username.length > 20) { setSaveError('Username must be 20 characters or fewer.'); return; }
    if (usernameStatus === 'taken')    { setSaveError('That username is already taken.'); return; }
    if (usernameStatus === 'checking') { setSaveError('Still checking availability — please wait.'); return; }

    setSaving(true);

    const { error } = await supabase
      .from('profiles')
      .update({
        first_name: firstName.trim(),
        last_name:  lastName.trim(),
        username,
        full_name:  `${firstName.trim()} ${lastName.trim()}`,
      })
      .eq('id', user.id);

    setSaving(false);

    if (error) {
      setSaveError('Could not save changes. Please try again.');
      return;
    }

    const usernameChanged = username !== savedUsername;

    // Update saved values to reflect what's now in the DB
    setSavedFirstName(firstName.trim());
    setSavedLastName(lastName.trim());
    setSavedUsername(username);

    setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 3000);
    setProfileMode('view');

    // Keep auth metadata in sync with profile
    await supabase.auth.updateUser({
      data: {
        first_name: firstName.trim(),
        last_name:  lastName.trim(),
        full_name:  `${firstName.trim()} ${lastName.trim()}`,
        username,
      },
    });

    if (usernameChanged) {
      navigate(`/profile/${username}`);
    }
  }

  // ── Delete flow: step 1 — verify password + username ────────
  // Called when the user submits the credentials form (deleteStep === 'open').
  // Verifies their password via Supabase auth. If correct, advances to the
  // final warning screen. Username match is enforced by button disabled state,
  // but we guard here too for safety.
  async function handleDeleteSubmit(e) {
    e.preventDefault();
    setDeleteError('');

    if (deleteUsername !== savedUsername) {
      setDeleteError('Username does not match. Please try again.');
      return;
    }

    if (!deletePassword) {
      setDeleteError('Please enter your password.');
      return;
    }

    setDeleting(true);
    const { error } = await supabase.auth.signInWithPassword({
      email:    user.email,
      password: deletePassword,
    });
    setDeleting(false);

    if (error) {
      setDeleteError('Incorrect password. Please try again.');
      return;
    }

    // Credentials verified — advance to final warning screen
    setDeleteStep('final');
    setDeleteError('');
  }

  // ── Delete flow: step 2 — final deletion ───────────────────
  // Called from the final warning screen after the user clicks the last confirm.
  async function handleDeleteConfirm() {
    setDeleting(true);
    setDeleteError('');

    const { error } = await supabase.rpc('delete_account');

    if (error) {
      setDeleteError('Could not delete your account. Please try again.');
      setDeleting(false);
      return;
    }

    await signOut();
    navigate('/');
  }

  function resetDeleteFlow() {
    setDeleteStep('idle');
    setDeletePassword('');
    setDeleteUsername('');
    setDeleteError('');
  }

  // ── Username status indicator ──────────────────────────────
  function UsernameStatus() {
    if (username === savedUsername || username.length < 3) {
      return <p className="settings-section-sub" style={{ marginTop: 4 }}>Letters, numbers, underscores · 3–20 chars</p>;
    }
    if (usernameStatus === 'checking') {
      return <p className="settings-username-status settings-username-status--checking">Checking…</p>;
    }
    if (usernameStatus === 'available') {
      return <p className="settings-username-status settings-username-status--available">✓ Available</p>;
    }
    if (usernameStatus === 'taken') {
      return <p className="settings-username-status settings-username-status--taken">✗ Already taken</p>;
    }
    return null;
  }

  return (
    <div className="settings-page">
      <div className="settings-inner">

        <h1 className="settings-title">Settings</h1>

        {/* Loading */}
        {loading && (
          <div className="settings-loading">
            <span>★</span><span>★</span><span>★</span>
          </div>
        )}

        {profileError && (
          <p className="settings-save-error">{profileError}</p>
        )}

        {!loading && !profileError && (
          <>
            {/* ── Profile section ─────────────────────────── */}
            <section className="settings-section">

              {/* Header row: title + Edit button (view mode only) */}
              <div className="settings-section-header">
                <h2 className="settings-section-title">Profile</h2>
                {profileMode === 'view' && (
                  <button
                    type="button"
                    className="settings-edit-btn"
                    onClick={() => setProfileMode('confirm')}
                  >
                    Edit
                  </button>
                )}
              </div>

              {/* ── VIEW MODE — read-only display ─────────── */}
              {profileMode === 'view' && (
                <>
                  <div className="settings-profile-fields">
                    <div className="settings-profile-row">
                      <div className="settings-profile-field">
                        <span className="settings-profile-label">First name</span>
                        <span className="settings-profile-value">{savedFirstName || '—'}</span>
                      </div>
                      <div className="settings-profile-field">
                        <span className="settings-profile-label">Last name</span>
                        <span className="settings-profile-value">{savedLastName || '—'}</span>
                      </div>
                    </div>
                    <div className="settings-profile-field">
                      <span className="settings-profile-label">Username</span>
                      <span className="settings-profile-value">{savedUsername || '—'}</span>
                    </div>
                  </div>
                  {saveSuccess && (
                    <p className="settings-save-success">✓ Profile updated</p>
                  )}
                </>
              )}

              {/* ── CONFIRM MODE — "are you sure?" prompt ─── */}
              {profileMode === 'confirm' && (
                <div className="settings-edit-confirm">
                  <p className="settings-delete-warning">
                    Do you want to edit your profile information?
                  </p>
                  <div className="settings-delete-actions">
                    <button
                      type="button"
                      className="settings-delete-cancel"
                      onClick={() => setProfileMode('view')}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="settings-edit-confirm-btn"
                      onClick={enterEditMode}
                    >
                      Yes, edit profile
                    </button>
                  </div>
                </div>
              )}

              {/* ── EDIT MODE — editable form ──────────────── */}
              {profileMode === 'edit' && (
                <form className="settings-form" onSubmit={handleSave} noValidate>

                  <div className="settings-name-row">
                    <FormField label="First name" htmlFor="settings-first-name">
                      <Input
                        id="settings-first-name"
                        type="text"
                        placeholder="Jane"
                        value={firstName}
                        onChange={e => setFirstName(e.target.value)}
                        autoComplete="given-name"
                      />
                    </FormField>

                    <FormField label="Last name" htmlFor="settings-last-name">
                      <Input
                        id="settings-last-name"
                        type="text"
                        placeholder="Smith"
                        value={lastName}
                        onChange={e => setLastName(e.target.value)}
                        autoComplete="family-name"
                      />
                    </FormField>
                  </div>

                  <FormField label="Username" htmlFor="settings-username">
                    <Input
                      id="settings-username"
                      type="text"
                      placeholder="janesmith"
                      value={username}
                      onChange={handleUsernameChange}
                      autoComplete="username"
                      maxLength={20}
                    />
                    <UsernameStatus />
                  </FormField>

                  {saveError && <p className="settings-save-error">{saveError}</p>}

                  <div className="settings-save-row">
                    <Button type="submit" disabled={saving}>
                      {saving ? 'Saving…' : 'Save changes'}
                    </Button>
                    <button
                      type="button"
                      className="settings-cancel-edit-btn"
                      onClick={cancelEdit}
                      disabled={saving}
                    >
                      Cancel
                    </button>
                  </div>

                </form>
              )}

            </section>

            {/* ── Account section ─────────────────────────── */}
            <section className="settings-section">
              <h2 className="settings-section-title">Account</h2>

              <FormField label="Email address" htmlFor="settings-email">
                <Input
                  id="settings-email"
                  type="email"
                  value={user?.email || ''}
                  readOnly
                />
              </FormField>

              <div className="settings-theme-row">
                <div>
                  <p className="settings-theme-label">Appearance</p>
                  <p className="settings-theme-sub">
                    Currently: {
                      theme === 'hacker'   ? 'Hacker mode'   :
                      theme === 'dark'     ? 'Dark mode'     :
                      theme === 'midnight' ? 'Midnight mode' :
                      theme === 'flower'   ? 'Flower mode'   :
                                            'Light mode'
                    }
                  </p>
                </div>
                <div className="settings-theme-actions">
                  <button
                    type="button"
                    className="settings-theme-toggle"
                    onClick={toggleTheme}
                  >
                    {theme === 'hacker'   ? '>_ Hacker'   :
                     theme === 'dark'     ? '☾ Dark'     :
                     theme === 'midnight' ? '★ Midnight' :
                     theme === 'flower'   ? '🌸 Flower'  :
                                           '☀ Light'}
                  </button>
                  <Link to="/appearance" className="settings-see-more-link">
                    See more →
                  </Link>
                </div>
              </div>
            </section>

            {/* ── Danger zone ─────────────────────────────── */}
            <section className="settings-section settings-danger">
              <h2 className="settings-section-title settings-danger-title">
                Danger zone
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                  style={{ marginLeft: 8, verticalAlign: 'middle', color: '#e53e3e' }}>
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                  <line x1="12" y1="9" x2="12" y2="13"/>
                  <line x1="12" y1="17" x2="12.01" y2="17"/>
                </svg>
              </h2>
              <p className="settings-section-sub">
                Permanently deletes your profile and login. Your match history will remain on the leaderboard shown as "Deleted User" to preserve its integrity.
              </p>

              {/* Step 1 — idle: show the delete button */}
              {deleteStep === 'idle' && (
                <button
                  type="button"
                  className="settings-delete-btn"
                  onClick={() => setDeleteStep('open')}
                >
                  Delete my account
                </button>
              )}

              {/* Step 2 — open: password + username confirmation form.
                  The delete button stays disabled/gray until the typed username
                  exactly matches the user's real username — browser auto-fill
                  can fill the password but cannot fill the username field. */}
              {deleteStep === 'open' && (
                <form className="settings-delete-confirm" onSubmit={handleDeleteSubmit} noValidate>
                  <p className="settings-delete-warning">
                    Enter your password and type your username to confirm you want to delete your account.
                  </p>

                  {/* Password — browser auto-fill is fine here */}
                  <FormField label="Password" error={deleteError}>
                    <Input
                      type="password"
                      placeholder="Your password"
                      value={deletePassword}
                      onChange={e => { setDeletePassword(e.target.value); setDeleteError(''); }}
                      autoComplete="current-password"
                    />
                  </FormField>

                  {/* Username — autocomplete off so it must be typed manually */}
                  <FormField label={`Type your username to confirm: ${savedUsername}`}>
                    <Input
                      type="text"
                      placeholder={savedUsername}
                      value={deleteUsername}
                      onChange={e => { setDeleteUsername(e.target.value); setDeleteError(''); }}
                      autoComplete="off"
                    />
                  </FormField>

                  <div className="settings-delete-actions">
                    <button
                      type="button"
                      className="settings-delete-cancel"
                      onClick={resetDeleteFlow}
                      disabled={deleting}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className={`settings-delete-confirm-btn${deleteUsername === savedUsername ? ' ready' : ''}`}
                      disabled={deleteUsername !== savedUsername || deleting}
                    >
                      {deleting ? 'Verifying…' : 'Permanently Delete Account'}
                    </button>
                  </div>
                </form>
              )}

              {/* Step 3 — final: last warning screen before actual deletion */}
              {deleteStep === 'final' && (
                <div className="settings-delete-confirm">
                  <div className="settings-delete-final-warning">
                    <p className="settings-delete-warning">
                      <strong>Are you absolutely sure?</strong> Your account{' '}
                      <strong>@{savedUsername}</strong> will be permanently deleted.
                      All your match history, ELO rating, and profile data will be
                      gone forever. This cannot be undone.
                    </p>
                  </div>
                  {deleteError && <p className="settings-save-error">{deleteError}</p>}
                  <div className="settings-delete-actions">
                    <button
                      type="button"
                      className="settings-delete-cancel"
                      onClick={resetDeleteFlow}
                      disabled={deleting}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="settings-delete-confirm-btn ready"
                      onClick={handleDeleteConfirm}
                      disabled={deleting}
                    >
                      {deleting ? 'Deleting…' : 'Yes, delete my account'}
                    </button>
                  </div>
                </div>
              )}
            </section>
          </>
        )}

      </div>
    </div>
  );
}

export default Settings;
